import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../common/Icons';
import './AdminModal.css';

export default function AdminModal({
  show,
  onHide,
  title,
  badge,
  subtitle,
  icon,
  size = 'md',
  footer,
  children,
  ariaLabelledBy = 'adm-modal-title',
  closeOnBackdrop = true
}) {
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') onHide();
    },
    [onHide]
  );

  useEffect(() => {
    if (!show) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [show, handleKeyDown]);

  if (!show) return null;

  return createPortal(
    <div
      className="adm-modal-overlay"
      onClick={closeOnBackdrop ? onHide : undefined}
      role="presentation"
    >
      <div
        className={`adm-modal adm-modal--${size}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={ariaLabelledBy}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="adm-modal__header">
          <div className="adm-modal__header-main">
            {icon && (
              <span className="adm-modal__header-icon" aria-hidden>
                <Icon name={icon} size={18} />
              </span>
            )}
            <div>
              {badge && <span className="adm-modal__badge">{badge}</span>}
              <h2 id={ariaLabelledBy}>{title}</h2>
              {subtitle && <p className="adm-modal__subtitle">{subtitle}</p>}
            </div>
          </div>
          <button
            type="button"
            className="adm-modal__close"
            onClick={onHide}
            aria-label="Fermer"
          >
            <Icon name="times" size={16} />
          </button>
        </header>

        <div className="adm-modal__body">{children}</div>

        {footer != null && <footer className="adm-modal__footer">{footer}</footer>}
      </div>
    </div>,
    document.body
  );
}

export function ModalDetailField({ label, children, fullWidth = false }) {
  return (
    <div className={`adm-modal-detail__field ${fullWidth ? 'adm-modal-detail__field--full' : ''}`}>
      <span className="adm-modal-detail__label">{label}</span>
      <div className="adm-modal-detail__value">{children}</div>
    </div>
  );
}

export function ModalFormSection({ title, hint, children }) {
  return (
    <section className="adm-modal-section">
      {title && (
        <div className="adm-modal-section__head">
          <h3 className="adm-modal-section__title">{title}</h3>
          {hint && <p className="adm-modal-section__hint">{hint}</p>}
        </div>
      )}
      {children}
    </section>
  );
}

export function ModalFormGroup({
  label,
  required,
  htmlFor,
  children,
  className = '',
  fullWidth = false,
  error
}) {
  return (
    <div
      className={`adm-modal-field ${fullWidth ? 'adm-modal-field--full' : ''} ${className}`.trim()}
    >
      {label && (
        <label className="adm-modal-field__label" htmlFor={htmlFor}>
          {label}
          {required && <span className="adm-modal-field__req" aria-hidden> *</span>}
        </label>
      )}
      {children}
      {error && (
        <p className="adm-modal-field__error" role="alert">
          <Icon name="exclamationCircle" size={12} />
          <span>{error}</span>
        </p>
      )}
    </div>
  );
}

export function ModalFooterActions({ onCancel, cancelLabel = 'Annuler', primary }) {
  return (
    <>
      <button type="button" className="adm-modal-btn adm-modal-btn--ghost" onClick={onCancel}>
        {cancelLabel}
      </button>
      {primary}
    </>
  );
}
